import React, { useEffect, useState } from "react";
import {
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  List,
} from "lucide-react";
import API from "../../../services/interceptor";

export default function DropdownManager() {
  // State for the list of categories, fetched from the API
  const [categories, setCategories] = useState([]);
  // State for the currently selected category
  const [category, setCategory] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal controls
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Form states
  const [newItem, setNewItem] = useState("");
  const [editingValue, setEditingValue] = useState("");

  // Search/filter
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // Stats
  const [stats, setStats] = useState({ total: 0, matched: 0 });

// Effect to fetch, filter, and sort all available categories on component mount
  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await API.get('/api/dropdown');
        if (res.data) {
          const ignoreKeys = ['_id', '__v', 'createdAt', 'updatedAt'];
          
          const fetchedKeys = Object.keys(res.data)
            .filter(key => !ignoreKeys.includes(key)) // Filter out specified keys
            .sort((a, b) => a.localeCompare(b)); // Sort keys alphabetically

          setCategories(fetchedKeys);

          if (fetchedKeys.length > 0) {
            setCategory(fetchedKeys[0]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError(err?.response?.data?.error || "Failed to load dropdown categories from the server.");
      }
    }
    fetchCategories();
  }, []); // Empty dependency array ensures this runs only once

  // Effect to fetch items whenever the selected category changes
  useEffect(() => {
    // Only run if a category has been selected
    if (category) {
      fetchItems(category);
      // Reset UI state for the new category
      setNewItem("");
      setEditingValue("");
      setSelectedIdx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Effect for client-side search
  useEffect(() => {
    const lower = (searchTerm || "").toLowerCase();
    const f = items.filter((it) => it.toLowerCase().includes(lower));
    setFiltered(f);
    setStats({ total: items.length, matched: f.length });
  }, [searchTerm, items]);

  function showMessage(msg, type = "success") {
    if (type === "success") setSuccess(msg);
    else setError(msg);
    setTimeout(() => {
      setSuccess("");
      setError("");
    }, 4000);
  }

  async function fetchItems(cat = category) {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/api/dropdown/${encodeURIComponent(cat)}`);
      const data = res.data?.[cat] || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to load items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddItem(e) {
    e?.preventDefault?.();
    const value = (newItem || "").trim();
    if (!value) return setError("Item value is required");
    setActionLoading(true);
    setError("");
    try {
      const res = await API.put(`/api/dropdown/${encodeURIComponent(category)}`, { item: value });
      setItems(res.data?.[category] || []);
      setNewItem("");
      setShowAddModal(false);
      showMessage("Item added successfully");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to add item");
    } finally {
      setActionLoading(false);
    }
  }

  function openEdit(idx) {
    setSelectedIdx(idx);
    setEditingValue(items[idx]);
    setShowEditModal(true);
    setError("");
  }

  function cancelEdit() {
    setSelectedIdx(null);
    setEditingValue("");
    setShowEditModal(false);
  }

  async function saveEdit() {
    const newValue = (editingValue || "").trim();
    if (!newValue) return setError("New value cannot be empty");
    const oldValue = items[selectedIdx];
    if (newValue === oldValue) return cancelEdit();
    setActionLoading(true);
    setError("");
    try {
      const res = await API.patch(`/api/dropdown/${encodeURIComponent(category)}`, { oldValue, newValue });
      setItems(res.data?.[category] || []);
      cancelEdit();
      showMessage("Item updated successfully");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to update item");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDelete(idx) {
    const value = items[idx];
    if (!window.confirm(`Are you sure you want to delete "${value}" from the "${category}" list?`)) return;
    setActionLoading(true);
    setError("");
    try {
      const res = await API.delete(`/api/dropdown/${encodeURIComponent(category)}/${encodeURIComponent(value)}`);
      setItems(res.data?.[category] || []);
      showMessage("Item deleted successfully");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to delete item");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <List className="h-8 w-8 text-sky-600" />
            Dropdown Manager
          </h1>
          <p className="text-gray-600 mt-1">Manage dropdown lists used across the application</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { fetchItems(category); setSuccess(''); setError(''); }}
            className="flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Item
          </button>
        </div>
      </div>
      
      {/* ... Other sections like Stats and Messages remain the same ... */}

      {/* Search + Category select */}
      <div className="bg-white p-6 rounded-lg border shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
            {/* ... Search input remains the same ... */}
          <div className="min-w-[220px] flex items-center gap-3">
            <label className="font-medium">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border rounded px-3 py-2 flex-1"
              disabled={categories.length === 0} // Disable while loading
            >
              {categories.length > 0 ? (
                categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))
              ) : (
                <option>Loading categories...</option>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Items list card */}
      <div className="bg-white rounded-lg border shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium">Items</h3>
          <div className="text-sm text-gray-500">{filtered.length} / {items.length}</div>
        </div>

        {loading ? (
          <div className="text-gray-500 py-12 text-center">Loading items...</div>
        ) : items.length === 0 ? (
          <div className="text-gray-500 py-12 text-center">No items in this category.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map((it, idx) => (
              <li key={idx} className="flex items-center justify-between border rounded p-2 hover:shadow-sm">
                <div className="break-words whitespace-pre-wrap w-0 flex-1 mr-4">{it}</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(items.indexOf(it))}
                    className="text-sm bg-yellow-100 px-2 py-1 rounded hover:bg-yellow-200"
                    disabled={actionLoading}
                  >
                    <Edit className="h-4 w-4 inline" />
                  </button>
                  <button
                    onClick={() => handleDelete(items.indexOf(it))}
                    className="text-sm bg-red-100 px-2 py-1 rounded hover:bg-red-200"
                    disabled={actionLoading}
                  >
                    <Trash2 className="h-4 w-4 inline" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Item</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleAddItem} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Type new item value"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
                <button type="submit" disabled={actionLoading} className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg">{actionLoading ? 'Adding...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Item</h3>
              <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={cancelEdit} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
                <button type="button" onClick={saveEdit} disabled={actionLoading} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">{actionLoading ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Replace Modal */}
      {/* {showReplaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Replace Entire Category</h3>
              <button onClick={() => setShowReplaceModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={replaceCategory} className="space-y-4">
              <p className="text-sm text-gray-500">Paste one item per line. This will overwrite all existing items in <span className="font-medium">{category}</span>.</p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={8}
                className="w-full border rounded px-3 py-2 font-mono text-sm"
                placeholder={`One item per line\nExample:\nStain on Teeth\nPain in Upper Left Teeth`}
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => setBulkText(items.join('\n'))} disabled={actionLoading} className="bg-gray-100 px-4 py-2 rounded">Load current items</button>
                <button type="submit" disabled={actionLoading} className="bg-amber-600 text-white px-4 py-2 rounded">{actionLoading ? 'Replacing...' : 'Replace Category'}</button>
              </div>
            </form>
          </div>
        </div>
      )} */}

      {/* Loading spinner */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      )}
    </div>
  );
}
